import React from 'react';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Space,
  Table,
  Tag,
  Typography,
  Popconfirm,
} from 'antd';
import { RollbackOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { EligibleItem, n5m3Api } from '../../services/n5m3Api';
import { useN5M3Reversal } from './hooks/useN5M3Reversal';
import { BatchAcceptanceBridge, pwn5m3f1AcceptanceManifest } from '../../features/acceptance';
import './N5M3Form.css';

const { Title, Text } = Typography;

function renderStatus(status: string): React.ReactNode {
  if (status === 'P') return <Tag color="blue">QA Approved</Tag>;
  if (status === 'N') return <Tag color="red">Reversed</Tag>;
  return <Tag color="default">{status || 'Unknown'}</Tag>;
}

const N5M3Form: React.FC = () => {
  const {
    loading,
    executing,
    items,
    total,
    selectedKeys,
    filter,
    setFilter,
    setSelectedKeys,
    fetchEligible,
    batchExecute,
  } = useN5M3Reversal();

  const columns: ColumnsType<EligibleItem> = [
    {
      title: <span data-contract-field="fn110_01">Complaint No</span>,
      dataIndex: 'fn110_01',
      key: 'fn110_01',
      width: 120,
      render: (text: string) => <Text code>{text}</Text>,
    },
    {
      title: <span data-contract-field="fn110_11">Date</span>,
      dataIndex: 'fn110_11',
      key: 'fn110_11',
      width: 110,
      render: (date: string) => date || '\u2014',
    },
    {
      title: <span data-contract-field="fn110_17">Amount</span>,
      dataIndex: 'fn110_17',
      key: 'fn110_17',
      width: 100,
      align: 'right',
      render: (v: number) => (v ? v.toFixed(2) : '0.00'),
    },
    {
      title: <span data-contract-field="fn110_22">Dept</span>,
      dataIndex: 'fn110_22',
      key: 'fn110_22',
      width: 70,
      align: 'center',
    },
    {
      title: <span data-contract-field="fn110_812">QA Status</span>,
      dataIndex: 'fn110_812',
      key: 'fn110_812',
      width: 100,
      align: 'center',
      render: renderStatus,
    },
    {
      title: <span data-contract-field="fn110_8121">QA Opinion</span>,
      dataIndex: 'fn110_8121',
      key: 'fn110_8121',
      width: 200,
      ellipsis: true,
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedKeys,
    onChange: (keys: React.Key[]) => setSelectedKeys(keys as string[]),
  };

  const handleSearch = () => { fetchEligible(); };

  const handleFilterChange = (field: 'startNo' | 'endNo', value: string) => {
    setFilter({ ...filter, [field]: value });
  };

  const handleStartBlur = () => {
    if (filter.startNo && !filter.endNo) {
      const autoEnd = (filter.startNo + 'zzzzzzzz').substring(0, 8);
      setFilter({ ...filter, endNo: autoEnd });
    }
  };

  return (
    <div style={{ padding: 16 }}>
      <Title level={4} style={{ marginBottom: 16 }}>Complaint QA Approval Reversal (pwn5m3f1)</Title>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Form layout="inline" size="small">
          <Form.Item label={<span data-contract-field="fn110_01">Doc No Start</span>}>
            <Input
              value={filter.startNo}
              onChange={(e) => handleFilterChange('startNo', e.target.value)}
              onBlur={handleStartBlur}
              placeholder="e.g. CN00001"
              style={{ width: 120 }}
              maxLength={8}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item label={<span data-contract-field="fn110_01">Doc No End</span>}>
            <Input
              value={filter.endNo}
              onChange={(e) => handleFilterChange('endNo', e.target.value)}
              placeholder="e.g. CN99999"
              style={{ width: 120 }}
              maxLength={8}
              onPressEnter={handleSearch}
            />
          </Form.Item>
          <Form.Item>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={handleSearch}
              loading={loading}
            >
              Search
            </Button>
          </Form.Item>
        </Form>
      </Card>

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row justify="space-between" align="middle">
          <Col>
            <Space>
              <Text type="secondary">
                Total: <strong>{total}</strong> eligible
              </Text>
              {selectedKeys.length > 0 && (
                <Text type="secondary">
                  Selected: <strong>{selectedKeys.length}</strong>
                </Text>
              )}
            </Space>
          </Col>
          <Col>
            <Popconfirm
              title={`Confirm reversal of ${selectedKeys.length} complaint(s)? This action cannot be undone.`}
              onConfirm={batchExecute}
              okText="Confirm Reversal"
              cancelText="Cancel"
              disabled={selectedKeys.length === 0}
              okButtonProps={{ danger: true }}
            >
              <Button
                danger
                icon={<RollbackOutlined />}
                loading={executing}
                disabled={selectedKeys.length === 0}
              >
                Batch Reversal
              </Button>
            </Popconfirm>
          </Col>
        </Row>
      </Card>

      <Table<EligibleItem>
        rowKey="fn110_01"
        columns={columns}
        dataSource={items}
        loading={loading}
        rowSelection={rowSelection}
        size="small"
        pagination={{
          pageSize: 50,
          showSizeChanger: false,
          showTotal: (t) => `Total ${t}`,
        }}
        scroll={{ x: 700 }}
        locale={{ emptyText: 'No eligible documents' }}
      />
      <BatchAcceptanceBridge
        manifest={pwn5m3f1AcceptanceManifest}
        records={items}
        selectedKeys={selectedKeys}
        keyOf={(item) => item.fn110_01}
        queryExact={(key) => n5m3Api.search({ startNo: key, endNo: key })}
        lastApiResponse={items}
        loading={loading || executing}
        onAutoFill={() => items[0] && setSelectedKeys([items[0].fn110_01])}
        autoFillDisabled={items.length === 0}
        screenDraft={filter}
      />
    </div>
  );
};

export default N5M3Form;
